import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Users as UsersIcon,
  Search,
  Filter,
  Edit,
  Trash2,
  UserPlus,
  CheckCircle,
  XCircle,
  ChevronRight
} from 'lucide-react';
import UserForm from './UserForm';
import SettingsHeader from '../../ui/SettingsHeader';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Loader from '../../ui/Loader';

const Users = () => {
  const { users: apiUsers, addUser, updateUser, deleteUser, loadUsers, loading } = useData();
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: ''
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, userId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Load users on mount
  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Ensure users is always an array
  const users = Array.isArray(apiUsers) ? apiUsers : [];

  const roles = [
    { value: 'all', label: 'All Staff' },
    { value: 'admin', label: 'Administrators' },
    { value: 'teacher', label: 'Teachers' }
  ];

  const statuses = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success-100 text-success-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusToggle = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      updateUser(userId, { status: user.status === 'active' ? 'inactive' : 'active' });
    }
  };

  const filteredUsers = users.filter(user => {
    // Only show admin and teacher users in Administration
    if (user.userRole !== 'admin' && user.userRole !== 'teacher') return false;
    if (filters.role !== 'all' && user.userRole !== filters.role) return false;
    if (filters.status !== 'all' && user.status !== filters.status) return false;
    if (filters.search && !user.userName?.toLowerCase().includes(filters.search.toLowerCase()) && !user.userEmail?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'teacher': return 'Teacher';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return { 
        backgroundColor: 'color-mix(in srgb, var(--secondary-color, #7c3aed) 10%, white)',
        color: 'var(--secondary-color, #7c3aed)'
      };
      case 'teacher': return { 
        backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)',
        color: 'var(--primary-color, #4f46e5)'
      };
      default: return { backgroundColor: '#f3f4f6', color: '#1f2937' };
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteModal({ open: true, userId });
  };

  const confirmDeleteUser = async () => {
    if (!deleteModal.userId) return;
    setIsDeleting(true);
    try {
      await deleteUser(deleteModal.userId);
      setDeleteModal({ open: false, userId: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleUserSubmit = (userData) => {
    if (selectedUser) {
      // Edit existing user
      updateUser(selectedUser._id, userData);
    } else {
      // Add new user
      addUser(userData);
    }
    setShowUserForm(false);
    setSelectedUser(null);
  };

  const handleCloseUserForm = () => {
    setShowUserForm(false);
    setSelectedUser(null);
  };

  // Temporarily removed admin check for testing

  if (loading.users) {
    return <Loader />;
  }

  return (
    <div className="space-y-4">
      <SettingsHeader
        title="Staff Management"
        showBack={false}
        icon={UsersIcon}
        buttons={[
          {
            label: "Add Staff Member",
            icon: UserPlus,
            onClick: handleAddUser,
            variant: "default",
          },
        ]}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative min-w-[140px]">
            <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none cursor-pointer hover:bg-white"
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronRight className="w-3.5 h-3.5 rotate-90" />
            </div>
          </div>

          <div className="relative min-w-[140px]">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none cursor-pointer hover:bg-white"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronRight className="w-3.5 h-3.5 rotate-90" />
            </div>
          </div>

          <div className="relative w-full sm:w-56">
            <Search className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2" />
            <input
              type="text"
              placeholder="Search staff..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all hover:bg-white"
            />
          </div>
        </div>
      </SettingsHeader>

      {/* Users List */}
      <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-bold text-gray-900">
            Users ({filteredUsers.length})
          </h3>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>Total: {users.length}</span>
            <span>•</span>
            <span>Active: {users.filter(u => u.status === 'active').length}</span>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-gray-900 mb-1">No users found</h3>
            <p className="text-xs text-gray-500">Try adjusting your filters or add new users.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Role & Details
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((userItem) => (
                  <tr key={userItem._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div 
                          className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs" 
                          style={{ 
                            backgroundColor: 'color-mix(in srgb, var(--primary-color), white 90%)', 
                            color: 'var(--primary-color)' 
                          }}
                        >
                            {userItem.userName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="ml-3">
                          <div className="text-xs font-bold text-gray-900">{userItem.userName || 'Unknown'}</div>
                          <div className="text-[10px] text-gray-500">ID: {userItem._id?.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2 mb-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium" style={getRoleColor(userItem.userRole)}>
                          {getRoleDisplayName(userItem.userRole)}
                        </span>
                      </div>
                     <div className="text-[10px] text-gray-500">
                          {userItem.userRole === 'teacher' && (
                            userItem.assignedClasses?.length > 0
                              ? `Classes: ${userItem.assignedClasses.map(c => c.unitName).join(', ')}`
                              : 'No classes assigned'
                          )}
                          {userItem.userRole === 'admin' && 'System Administrator'}
                        </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs text-gray-900">{userItem.userEmail || 'N/A'}</div>
                      <div className="text-[10px] text-gray-500">{userItem.userPhoneNumber || 'N/A'}</div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getStatusColor(userItem.status || 'active')}`}>
                        {userItem.status || 'active'}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right text-xs font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleStatusToggle(userItem._id)}
                          className={`p-1.5 rounded transition-colors ${
                            (userItem.status || 'active') === 'active'
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={(userItem.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {(userItem.status || 'active') === 'active' ? <XCircle className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                        </button>

                        <button
                          onClick={() => handleEditUser(userItem)}
                          className="p-1.5 text-gray-400 rounded transition-colors hover:bg-gray-100"
                          onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                          onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                          title="Edit user"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>

                        <button
                          onClick={() => handleDeleteUser(userItem._id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete user"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3 p-3">
              {filteredUsers.map((userItem) => (
                <div key={userItem._id} className="bg-white border border-gray-200 rounded p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs" 
                        style={{ 
                          backgroundColor: 'color-mix(in srgb, var(--primary-color), white 90%)', 
                          color: 'var(--primary-color)' 
                        }}
                      >
                          {userItem.userName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">{userItem.userName || 'Unknown'}</div>
                        <div className="text-[10px] text-gray-500">ID: {userItem._id?.substring(0, 8)}...</div>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium" style={getRoleColor(userItem.userRole)}>
                      {getRoleDisplayName(userItem.userRole)}
                    </span>
                  </div>

                  <div className="space-y-1 mb-2">
                    <div className="text-xs text-gray-900">
                      {userItem.userRole === 'teacher' && (userItem.assignedClasses?.length > 0 ? `Classes: ${userItem.assignedClasses.map(c => c.unitName).join(', ')}` : 'No classes assigned')}
                      {userItem.userRole === 'admin' && 'System Administrator'}
                    </div>
                    <div className="text-xs text-gray-900">{userItem.userEmail || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{userItem.userPhoneNumber || 'N/A'}</div>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getStatusColor(userItem.status || 'active')}`}>
                      {userItem.status || 'active'}
                    </span>

                    <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleStatusToggle(userItem._id)}
                          className={`p-1.5 rounded transition-colors ${
                            (userItem.status || 'active') === 'active'
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                        >
                          {(userItem.status || 'active') === 'active' ? <XCircle className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                        </button>

                        <button
                          onClick={() => handleEditUser(userItem)}
                          className="p-1.5 text-gray-400 rounded transition-colors hover:bg-gray-100"
                          onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'}
                          onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>

                        <button
                          onClick={() => handleDeleteUser(userItem._id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={selectedUser}
          onClose={handleCloseUserForm}
          onSubmit={handleUserSubmit}
          isSubmitting={false} // Would suggest adding a loading state for this form too
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, userId: null })}
        onConfirm={confirmDeleteUser}
        title="Delete Staff Member"
        message="Are you sure you want to delete this staff member? This action cannot be undone and they will lose all access immediately."
        confirmText="Delete User"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Users;
